import {
  Box, Button, Grid, Link, Stack,
} from '@mui/material';
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { v4 as uuid } from 'uuid';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import EditableText from '../../Form/EditableText';
import Utils from '../../../utils/Utils';
import '../../../assets/css/menu-list.css';
import BlockMenuListData from './BlockMenuListData';
import SubBlockMenuData from './SubBlockMenuData';
import BlockProps from '../../../models/BlockProps';
import CloseButton from '../../Buttons/Close';
import BlockData from '../../../models/BlockData';
import LinkButton from '../../Buttons/LinkButton';
import CroppedImage from '../../CroppedImage';
import ImageSettingsModal from '../../Modals/ImageSettingsModal/ImageSettingsModal';
import BlockComponentImage from '../../../models/BlockComponentValues/BlockComponentImage';
import config from '../../../config';
import BackGroundImageModal from '../../Modals/ImageModal/BackGroundImageModal';
import useGetBackground from '../../../hooks/UseGetBackground';
import useParallaxScript from '../../../hooks/useParallaxScript';
import ImageEnlargeModal from '../../Images/ImageEnlargeModal';

export interface BlockMenuListProps extends BlockProps {
  blockData: BlockData[]
  showImageModal?: boolean;
  onCloseImageModal?: () => void;
}

function BlockMenuList({
  data,
  isEditing,
  isTemplate,
  blockData,
  editHandler,
  showImageModal,
  onCloseImageModal,
  mainColor,
}: BlockMenuListProps) {
  const targetModalId = uuid();

  const [props, setProps] = useState<BlockMenuListData>(data.data as BlockMenuListData);

  useEffect(() => {
    setProps(data.data as BlockMenuListData);
  }, [data.data]);

  const [openTitleBackgroundModal, setOpenTitleBackgroundModal] = useState(false);
  const [openIsEditableText, setOpenIsEditableText] = useState(false);

  const handleTitleUpdate = (text?: string) => {
    props.title.value = text;
    editHandler(data.id, props);
  };

  const handleAddSubBlock = () => {
    props.menus.push(SubBlockMenuData.getDefault(undefined, config.defaultImageUrl));
    editHandler(data.id, props);
  };

  const handleRemoveSubBlock = useCallback((index: number) => {
    props.menus.splice(index, 1);
    editHandler(data.id, props);
  }, [props, editHandler, data.id]);

  const handleChangeTitleBackground = () => {
    if (isEditing && !isTemplate) {
      // setOpenTitleBackgroundModal(true);
      if (openIsEditableText) {
        setOpenTitleBackgroundModal(false);
      } else {
        setOpenTitleBackgroundModal(true);
      }
    }
  };

  const backgroundStyles = useGetBackground(props.titleImage?.solidColor, props.titleImage?.value);

  const menuItems = useMemo(() => {
    if (props.menus && props.menus?.length > 0) {
      return props.menus?.map((menu, index) => (
        <Grid
          key={menu.id}
          item
          minWidth={300}
          sx={[Utils.hoverBorder(isEditing), {
            position: 'relative',
            padding: 3,
            flexGrow: 1,
            maxWidth: '100%',
          }]}
        >
          <CloseButton
            show={isEditing}
            updateHandler={() => handleRemoveSubBlock(index)}
          />

          {props.menus[index]?.image?.value !== undefined && (
            <Box
              sx={[Utils.hoverBorder(isEditing), {
                display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: 'auto',
              }]}
            >
              <label htmlFor={`${data.id}_menu${index}`} style={{ width: '100%', height: 'auto' }}>
                <ImageSettingsModal
                  isEditing={isEditing}
                  image={props.menus[index].image}
                  disabled={isTemplate || !isEditing}
                  blockData={blockData}
                  onConfirm={(newImage: BlockComponentImage) => {
                    props.menus[index].image = newImage;
                    editHandler(data.id, props);
                  }}
                >
                  {(!isEditing) ? (
                    <Link
                      style={{ cursor: props.menus[index].image?.link?.linkData?.url ? 'pointer' : 'default' }}
                      target={props.menus[index].image?.link?.linkData?.openType}
                      href={props.menus[index].image?.link?.linkData?.url || undefined}
                      sx={{
                        width: '100%',
                        height: '100%',
                      }}
                      rel="noreferrer"
                    >
                      <CroppedImage
                        isEditing={isEditing}
                        targetModalId={targetModalId}
                        image={props.menus[index].image}
                        settings={props.menus[index].image.settings}
                      />
                    </Link>
                  ) : (
                    <CroppedImage
                      isEditing={isEditing}
                      targetModalId={targetModalId}
                      image={props.menus[index].image}
                      settings={props.menus[index].image.settings}
                    />
                  )}
                </ImageSettingsModal>
              </label>
              <CloseButton
                show={isEditing}
                updateHandler={() => {
                  props.menus[index].image.value = undefined;
                  editHandler(data.id, props);
                }}
              />
            </Box>
          )}

          <Grid
            container
            spacing={0}
            direction="column"
            alignItems="center"
            justifyContent="center"
          >

            {props.menus[index].title.value && (
              <Box sx={[Utils.hoverBorder(isEditing), { marginTop: 2 }]}>
                <CloseButton
                  show={isEditing}
                  updateHandler={() => {
                    props.menus[index].title.value = undefined;
                    editHandler(data.id, props);
                  }}
                />
                <EditableText
                  text={menu.title.value}
                  textColor={menu.title.settings?.textColor}
                  isEditable={isEditing && !isTemplate}
                  updateHandler={(text) => {
                    props.menus[index].title.value = text;
                    editHandler(data.id, props);
                  }}
                />
              </Box>
            )}

            {props.menus[index].price.value && (
              <Box sx={[Utils.hoverBorder(isEditing), { marginTop: 2 }]}>
                <CloseButton
                  show={isEditing}
                  updateHandler={() => {
                    props.menus[index].price.value = undefined;
                    editHandler(data.id, props);
                  }}
                />
                <EditableText
                  text={menu.price.value}
                  textColor={menu.price.settings?.textColor}
                  isEditable={isEditing && !isTemplate}
                  updateHandler={(text) => {
                    props.menus[index].price.value = text;
                    editHandler(data.id, props);
                  }}
                />
              </Box>
            )}

          </Grid>

          {props.menus[index].description.value && (
            <Box sx={[Utils.hoverBorder(isEditing), { marginTop: 2 }]}>
              <CloseButton
                show={isEditing}
                updateHandler={() => {
                  props.menus[index].description.value = undefined;
                  editHandler(data.id, props);
                }}
              />
              <EditableText
                text={menu.description.value}
                textColor={menu.description.settings?.textColor}
                isEditable={isEditing && !isTemplate}
                updateHandler={(text) => {
                  props.menus[index].description.value = text;
                  editHandler(data.id, props);
                }}
              />
            </Box>
          )}

          {props.menus[index].button && (
            <Box sx={[Utils.hoverBorder(isEditing), { marginTop: 2 }]}>
              <CloseButton
                show={isEditing}
                updateHandler={() => {
                  props.menus[index].button = undefined;
                  editHandler(data.id, props);
                }}
              />
              <LinkButton
                mainColor={mainColor}
                button={menu.button}
                blockData={blockData}
                isEditable={isEditing && !isTemplate}
                isTemplate={isTemplate}
                updateHandler={(item) => {
                  props.menus[index].button = item;
                  editHandler(data.id, props);
                }}
              />
            </Box>
          )}

        </Grid>
      ));
    }

    return [];
  }, [props, isEditing, data.id, isTemplate, blockData, mainColor, handleRemoveSubBlock, editHandler]);

  const elementUuid = uuid();
  useParallaxScript(props.backgroundImage?.settings?.parallaxBool, elementUuid, config.backgroundImage.parallaxEffect);

  return (
    <>
      <Grid
        id={elementUuid}
        container
        spacing={0}
        direction="column"
        alignItems="center"
        sx={{
          minHeight: '250px',
          overflow: 'hidden',
          py: 1.5,
          ...useGetBackground(
            props.backgroundImage?.solidColor,
            props.backgroundImage?.value,
            props.backgroundImage?.settings?.scale,
            props.backgroundImage?.settings?.scaleBool,
          ),
          backgroundPosition: 'center 0px',
        }}
      >
        {(isEditing && !isTemplate) && (
          <>
            <BackGroundImageModal
              backgroundImage={props.backgroundImage}
              isShowModal={showImageModal}
              onCloseModal={onCloseImageModal}
              handleOnConfirm={(url: BlockComponentImage) => {
                const tempBg = { ...props, backgroundImage: { ...url } };
                setProps(tempBg);
                editHandler(data.id, tempBg);
              }}
            />
            <BackGroundImageModal
              backgroundImage={props.titleImage}
              isShowModal={openTitleBackgroundModal}
              onCloseModal={() => setOpenTitleBackgroundModal(false)}
              handleOnConfirm={(titleImage: BlockComponentImage) => {
                const tempTitleBg = { ...props, titleImage: { ...titleImage } };
                setProps(tempTitleBg);
                editHandler(data.id, tempTitleBg);
              }}
            />
          </>
        )}

        <Grid
          container
          spacing={0}
          direction="column"
          alignItems="center"
          justifyContent="center"
          className="block-content-wrapper"
        >
          {props.title?.value && (
            <Box
              sx={{
                width: '100%',
                paddingX: isEditing ? '12px' : '0px',
                marginTop: 2,
              }}
            >
              <Grid
                container
                spacing={0}
                direction="column"
                alignItems="center"
                justifyContent="center"
                onClick={handleChangeTitleBackground}
                sx={
                  [Utils.hoverBorder(isEditing), {
                    padding: isEditing ? 1 : 0,
                  }]
                }
              >
                <CloseButton
                  show={isEditing}
                  updateHandler={() => {
                    props.title = undefined;
                    editHandler(data.id, props);
                  }}
                />
                <Grid
                  item
                  alignItems="center"
                  xs={12}
                  sx={
                    [Utils.hoverBorder(isEditing), {
                      ...backgroundStyles,
                      minHeight: '46px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                    }]
                  }
                >
                  {props.title?.value && (
                    <Box sx={Utils.hoverBorder(isEditing)}>
                      <EditableText
                        text={props.title?.value}
                        textColor={props.title?.settings?.textColor}
                        isEditable={isEditing && !isTemplate}
                        updateHandler={handleTitleUpdate}
                        onChangeModalOpen={(isOpen) => setOpenIsEditableText(isOpen)}
                      />
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}

          <Grid
            container
            spacing={0}
            direction="row"
            alignItems="stretch"
            justifyContent="center"
            sx={{
              marginTop: 4,
              paddingX: '12px',
            }}
          >
            {menuItems}
          </Grid>
        </Grid>
        {isEditing && (
          <Stack spacing={0} sx={{ border: '1px dashed lightGray', marginTop: '12px', width: 'calc(100% - 24px)' }}>
            <Button color="primary" onClick={handleAddSubBlock}>
              <AddCircleRoundedIcon />
            </Button>
          </Stack>
        )}
      </Grid>

      <ImageEnlargeModal
        images={[...props.menus.map((menu) => menu.image)]}
        targetModalId={targetModalId}
      />
    </>
  );
}

export default BlockMenuList;
